"use client";
import { cn } from "@/lib/utils";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import React, { useRef, useState, useEffect, useCallback } from "react";

interface NavbarProps {
  children: React.ReactNode;
  className?: string;
}

interface NavBodyProps {
  children: React.ReactNode;
  className?: string;
  visible?: boolean;
}

interface NavItemsProps {
  items: {
    name: string;
    link: string;
  }[];
  className?: string;
  onItemClick?: (link: string) => void;
}

interface MobileNavProps {
  children: React.ReactNode;
  className?: string;
  visible?: boolean;
}

interface MobileNavHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface MobileNavMenuProps {
  children: React.ReactNode;
  className?: string;
  isOpen: boolean;
  onClose: () => void;
}

export const Navbar = ({ children, className }: NavbarProps) => {
  const [visible, setVisible] = useState(false);
  const ticking = useRef(false);

  const onScroll = useCallback(() => {
    if (!ticking.current) {
      ticking.current = true;
      requestAnimationFrame(() => {
        setVisible(window.scrollY > 100);
        ticking.current = false;
      });
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  return (
    <div
      className={cn("fixed inset-x-0 top-0 lg:top-0 z-50 w-full", className)}
      style={{ top: "var(--public-app-banner-offset, 0px)" }}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(
              child as React.ReactElement<{ visible?: boolean }>,
              { visible }
            )
          : child
      )}
    </div>
  );
};

export const NavBody = ({ children, className, visible }: NavBodyProps) => {
  return (
    <div
      className={cn(
        "relative z-[60] mx-auto hidden w-full max-w-7xl flex-row items-center justify-between rounded-full px-8 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.4)] lg:flex",
        "border border-white/10 bg-[hsl(220,25%,8%)]/95 backdrop-blur-xl",
        "transition-[width,transform,backdrop-filter] duration-300 ease-out",
        visible ? "w-[88%] translate-y-5" : "w-[90%] translate-y-3",
        className
      )}
    >
      {children}
    </div>
  );
};

export const NavItems = ({ items, className, onItemClick }: NavItemsProps) => {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <div
      onMouseLeave={() => setHovered(null)}
      className={cn(
        "absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 flex-row items-center justify-center space-x-1 text-sm font-medium lg:flex",
        className
      )}
    >
      {items.map((item, idx) => (
        <button
          key={`nav-item-${idx}`}
          onMouseEnter={() => setHovered(idx)}
          onClick={() => onItemClick?.(item.link)}
          className="relative whitespace-nowrap px-3 py-2 text-white/70 transition-colors hover:text-white"
        >
          {hovered === idx && (
            <span className="absolute inset-0 rounded-full bg-white/10 animate-fade-in" />
          )}
          <span className="relative z-10">{item.name}</span>
        </button>
      ))}
    </div>
  );
};

export const MobileNav = ({ children, className, visible }: MobileNavProps) => {
  return (
    <div
      className={cn(
        "relative z-[60] mx-auto flex w-full max-w-[calc(100%-2rem)] flex-col rounded-2xl px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.4)] lg:hidden",
        "border border-white/10 bg-[hsl(220,25%,8%)]/95 backdrop-blur-xl",
        "transition-[width,transform] duration-300 ease-out",
        visible ? "w-[90%] translate-y-4" : "w-[95%] translate-y-2",
        className
      )}
    >
      {children}
    </div>
  );
};

export const MobileNavHeader = ({
  children,
  className,
}: MobileNavHeaderProps) => {
  return (
    <div className={cn("flex w-full items-center justify-between", className)}>
      {children}
    </div>
  );
};

export const MobileNavMenu = ({
  children,
  className,
  isOpen,
  onClose,
}: MobileNavMenuProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
          className={cn(
            "mt-4 max-h-[calc(100vh-7rem)] overflow-hidden",
            className
          )}
        >
          <div className="flex flex-col gap-2 overflow-y-auto overscroll-contain pr-1">
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const MobileNavToggle = ({
  isOpen,
  onClick,
}: {
  isOpen: boolean;
  onClick: () => void;
}) => {
  const Icon = isOpen ? X : Menu;

  return (
    <button
      type="button"
      aria-label={isOpen ? "Close menu" : "Open menu"}
      aria-expanded={isOpen}
      onClick={onClick}
      className="flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white"
    >
      <Icon className="h-5 w-5" />
    </button>
  );
};

export const NavbarLogo = ({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick?: () => void;
}) => {
  return (
    <button
      onClick={onClick}
      className="relative z-20 flex items-center gap-2 text-lg font-bold text-white"
    >
      {children}
    </button>
  );
};

export const NavbarButton = ({
  onClick,
  children,
  className,
  variant = "primary",
}: {
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  variant?: "primary" | "secondary" | "cta";
}) => {
  const variantStyles = {
    primary:
      "bg-white text-[hsl(220,25%,8%)] shadow-[0_2px_8px_rgba(0,0,0,0.15)] hover:bg-white/90",
    secondary:
      "bg-transparent text-white/80 hover:text-white hover:bg-white/10",
    cta: "bg-[hsl(175,80%,45%)] text-[hsl(220,25%,8%)] shadow-[0_2px_12px_rgba(45,212,191,0.3)] hover:bg-[hsl(175,80%,50%)]",
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        "relative z-20 cursor-pointer rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5",
        variantStyles[variant],
        className
      )}
    >
      {children}
    </button>
  );
};
